import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  MenuItem,
  Box,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  Info as InfoIcon,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
  SquareFoot as SquareFootIcon,
  // Hotel as HotelIcon,
  // Bathtub as BathtubIcon,
  LocationCity as CityIcon,
  Map as MapIcon,
  Notes as NotesIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Container from '../../../components/layout/Container';
import Fieldset from '../../../components/Form/Fieldset';
import Row from '../../../components/Form/Row';
import Form from '../../../components/Form/Form';
import ZipCodeInput from '../../../components/inputs/ZipCodeInput';
import NumberInput from '../../../components/inputs/NumberInput';
import AppBarComponent from '../../../components/AppBarComponent';
import ButtonArea from '../../../components/Form/ButtonArea';

const ButtonAreaStyled = styled(ButtonArea)`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const StyledFieldset = styled(Fieldset)`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.12);
  }
`;

const SectionTitle = styled(Typography)`
  color: #424242;
  font-weight: 600;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
`;

const FormTitle = styled(Typography)`
  color: #1976d2;
  font-weight: 600;
  margin-bottom: 32px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const propertyTypes = ['Apartamento', 'Casa', 'Terreno', 'Comercial', 'Outro'];
const propertyConditions = ['Novo', 'Usado', 'Reformado', 'Em construção', 'Para reformar'];

const buildApiUrl = (endpoint) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    console.error('API_BASE_URL não está definido no arquivo .env');
    return endpoint;
  }
  const normalizedBase = baseUrl.replace(/\/+$/, '') + '/';
  return new URL(endpoint, normalizedBase).toString();
};

export default function EditarImovelPage() {
  const { title } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    reset,
    watch,
    setValue
  } = useForm({
    mode: 'onChange'
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(true);
  const [imovelData, setImovelData] = useState(null);

  useEffect(() => {
    const loadPropertyData = async () => {
      try {
        setIsLoading(true);
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user?.token) {
          throw new Error('Usuário não autenticado');
        }

        // Tenta usar os dados passados pelo state primeiro
        if (location.state?.imovelData) {
          const data = location.state.imovelData;
          setImovelData(data);
          setFormData(data);
          setIsLoading(false);
          return;
        }

        // Busca o imóvel pelo título usando o endpoint /list
        const response = await axios.get(
          buildApiUrl('real-estate/list'),
          {
            params: {
              searchTerm: title,
              size: 1,
              page: 1
            },
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          }
        );

        if (response.data?.data?.length > 0) {
          const data = response.data.data[0];
          setImovelData(data);
          setFormData(data);
        } else {
          throw new Error('Nenhum imóvel encontrado com este título');
        }
      } catch (error) {
        console.error('Erro ao carregar imóvel:', error);
        setSnackbarMessage(error.response?.data?.message || error.message || 'Erro ao carregar dados do imóvel');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        navigate('/real-estate/management', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    const setFormData = (data) => {
      reset({
        title: data.title || '',
        propertyType: data.type || 'Apartamento',
        description: data.description || '',
        price: data.price?.toString() || '0',
        area: data.squareMeters?.toString() || '0',
        bedrooms: data.typology?.toString() || '0',
        bathrooms: data.bathrooms?.toString() || '0',
        condition: data.condition || 'Novo',
        address: data.address?.street || '',
        number: data.address?.number || '',
        city: data.address?.city || '',
        state: data.address?.state || '',
        country: 'Portugal',
        zipCode: data.address?.zipCode || '',
        complement: data.address?.complement || '',
        neighborhood: data.address?.neighborhood || ''
      });
    };

    loadPropertyData();
  }, [title, reset, navigate, location.state]);

  useEffect(() => {
    setValue('country', 'Portugal');
  }, [setValue]);

  const onSubmit = async (data) => {
    try {
      // setIsLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.token) {
        throw new Error('Usuário não autenticado');
      }

      setSnackbarMessage('Salvando alterações...');
      setSnackbarSeverity('info');
      setOpenSnackbar(true);

      const payload = {
        title: data.title.trim(),
        type: data.propertyType,
        description: data.description.trim(),
        price: parseFloat(data.price),
        squareMeters: parseFloat(data.area),
        typology: data.bedrooms.toString(),
        bathrooms: parseInt(data.bathrooms),
        condition: data.condition,
        address: {
          street: data.address.trim(),
          number: data.number,
          complement: data.complement.trim(),
          neighborhood: data.neighborhood.trim(),
          city: data.city.trim(),
          state: data.state.trim().toUpperCase(),
          country: 'Portugal',
          zipCode: data.zipCode.replace(/\D/g, '')
        }
      };

      await axios.patch(
        buildApiUrl(`real-estate/${encodeURIComponent(title)}`),
        payload,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSnackbarMessage('Imóvel atualizado com sucesso!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Erro ao atualizar imóvel:', error);
      setSnackbarMessage(error.response?.data?.message || error.message || 'Erro ao atualizar imóvel');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const descriptionLength = watch('description')?.length || 0;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBarComponent 
        title="Edição de Imóvel" 
        leftIcon={<ArrowBackIcon />} 
        onLeftIconClick={() => navigate('/real-estate/management')}
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
          <FormTitle variant="h1">
            <HomeIcon fontSize="large" />
            {imovelData ? `Editar Imóvel: ${imovelData.title}` : 'Editar Imóvel'}
          </FormTitle>
          
          <Typography variant="body1" color="text.secondary" mb={4}>
            Atualize os dados do imóvel abaixo. Todos os campos marcados com * são obrigatórios.
          </Typography>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <StyledFieldset>
              <SectionTitle variant="h2">
                <ApartmentIcon color="primary" />
                Informações Básicas
              </SectionTitle>
              
              <Row columns={2}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ 
                    required: 'Título é obrigatório',
                    minLength: {
                      value: 5,
                      message: 'Título deve ter pelo menos 5 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Título do Imóvel *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Apartamento com vista para o mar"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="propertyType"
                  control={control}
                  rules={{ required: 'Tipo de imóvel é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Tipo de Imóvel *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ApartmentIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {propertyTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Row>

              <Row columns={3}>
                <NumberInput
                  control={control}
                  name="price"
                  label="Preço *"
                  required
                  min={0}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                  placeholder="Ex: 500000"
                  startAdornment={<AttachMoneyIcon color={errors.price ? 'error' : 'action'} />}
                  prefix="€ "
                />
                <NumberInput
                  control={control}
                  name="area"
                  label="Área *"
                  required
                  min={1}
                  error={!!errors.area}
                  helperText={errors.area?.message}
                  placeholder="Ex: 80"
                  startAdornment={<SquareFootIcon color={errors.area ? 'error' : 'action'} />}
                  suffix=" m²"
                />
                <Controller
                  name="condition"
                  control={control}
                  rules={{ required: 'Condição do imóvel é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Condição *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {propertyConditions.map((condition) => (
                        <MenuItem key={condition} value={condition}>
                          {condition}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Row>

              {/* <Row columns={3}>
                <NumberInput
                  control={control}
                  name="bedrooms"
                  label="Quartos *"
                  required
                  min={0}
                  error={!!errors.bedrooms}
                  helperText={errors.bedrooms?.message}
                  placeholder="Ex: 3"
                  startAdornment={<HotelIcon color={errors.bedrooms ? 'error' : 'action'} />}
                />
                <NumberInput
                  control={control}
                  name="bathrooms"
                  label="Banheiros *"
                  required
                  min={0}
                  error={!!errors.bathrooms}
                  helperText={errors.bathrooms?.message}
                  placeholder="Ex: 2"
                  startAdornment={<BathtubIcon color={errors.bathrooms ? 'error' : 'action'} />}
                />
              </Row> */}
            </StyledFieldset>

            <StyledFieldset>
              <SectionTitle variant="h2">
                <MapIcon color="primary" />
                Endereço
                <Tooltip title="Informações completas do endereço do imóvel">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>
              
              <Row columns={2}>
                <Controller
                  name="address"
                  control={control}
                  rules={{ 
                    required: 'Endereço é obrigatório',
                    minLength: {
                      value: 5,
                      message: 'Endereço deve ter pelo menos 5 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Endereço *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Rua das Flores, 123"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <NumberInput
                  control={control}
                  name="number"
                  label="Número *"
                  required
                  min={1}
                  error={!!errors.number}
                  helperText={errors.number?.message}
                  placeholder="Ex: 123"
                  startAdornment={<HomeIcon color={errors.number ? 'error' : 'action'} />}
                />
              </Row>

              <Row columns={2}>
                <Controller
                  name="complement"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Complemento"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Apt 101, Bloco B"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="neighborhood"
                  control={control}
                  rules={{ 
                    required: 'Bairro é obrigatório',
                    minLength: {
                      value: 3,
                      message: 'Bairro deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Bairro *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Centro"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={3}>
                <ZipCodeInput
                  control={control}
                  name="zipCode"
                  label="CEP *"
                  required
                  error={!!errors.zipCode}
                  helperText={errors.zipCode?.message || 'Ex: 0000-000'}
                  placeholder="0000-000"
                  startAdornment={<MapIcon color={errors.zipCode ? 'error' : 'action'} />}
                />
                <Controller
                  name="city"
                  control={control}
                  rules={{ 
                    required: 'Cidade é obrigatória',
                    minLength: {
                      value: 3,
                      message: 'Cidade deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Cidade *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Lisboa"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CityIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="state"
                  control={control}
                  rules={{ 
                    required: 'Distrito é obrigatório',
                    minLength: {
                      value: 3,
                      message: 'Distrito deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Distrito *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Lisboa"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={1}>
                <Controller
                  name="country"
                  control={control}
                  defaultValue="Portugal"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="País"
                      fullWidth
                      disabled
                      value="Portugal"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            <StyledFieldset>
              <SectionTitle variant="h2">
                <DescriptionIcon color="primary" />
                Descrição
                <Tooltip title="Descreva o imóvel em detalhes para atrair compradores">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>
              <Row columns={1}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 1000,
                      message: 'Descrição deve ter no máximo 1000 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Descrição Detalhada"
                      multiline
                      rows={4}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || `${descriptionLength}/1000 caracteres`}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Descreva o imóvel em detalhes (opcional)"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <NotesIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            <ButtonAreaStyled>
              <Button
                type="button"
                variant="outlined"
                onClick={() => navigate('/real-estate/management')}
                sx={{ 
                  minWidth: 120,
                  px: 3,
                  py: 1,
                  borderRadius: 2
                }}
                startIcon={<ArrowBackIcon />}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || !isValid}
                sx={{ 
                  minWidth: 200,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Salvar Alterações'
                )}
              </Button>
            </ButtonAreaStyled>
          </Form>
        </Paper>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}